// SU cohort roster loader — v0M1.9.17.
//
// Loads the 6 basic v1 cohorts from src/data/generated/dials.json
// (produced by scripts/convert-xlsx.ts against docs/Balance.xlsx).
// Every faction can recruit all 6 per ECONOMY.md §4.1 + UNITS.md §1.2.
//
// The runtime CohortStats block (BATTLES.md §2.7.8, src/sim/battle-state.ts)
// is populated from CohortSpec.attack/defence/armor/... at recruitment time.
// Recruitment costs (Gold/Iron/Wood/Horses/Food/Population) are drawn from
// the faction resource ledgers per ECONOMY.md §4.

import { assertSchemaVersion, BALANCE_SCHEMA_VERSION } from './loader';
import rawBlob from './generated/dials.json';

export interface CohortSpec {
  /** Cohort tag list per BATTLES.md §2.7.8.2.a. */
  readonly tags: readonly string[];
  /** Combat stats — cohort-level per BATTLES.md §2.7.8. */
  readonly attack: number;
  readonly defence: number;
  readonly armor: number;
  readonly initiative: number;
  readonly chargeBonus: number;
  readonly missileAttack: number;
  readonly missileRange: number;
  readonly missileSupply: number;
  readonly maxHealthPerFigure: number;
  readonly figureCount: number;
  /**
   * v0M4.0j.2 — Battlefield movement rate in squares per round.
   * Default 1 (all non-cavalry). Cavalry cohorts get 3. Clamped to 1
   * when the cohort stands on Hills/Marsh/Forest per BATTLES.md §2.8.6.3.
   * Session-27 (2026-07-24): xlsx-authoritative (was hardcoded `?? 1`
   * runtime fallback before Michael authored the moveSpeedCombat column).
   */
  readonly moveSpeedCombat: number;
  /**
   * Session-27 (2026-07-24) — Strategic-map march speed in cells/tick.
   * Army-level effective speed = MIN across live cohorts per MECHANICS.md
   * §3.1 slowest-cohort rule. Blank xlsx cell → 0.5 fallback matching
   * the pre-session-27 DEFAULT_ARMY_SPEED_CELLS_PER_TICK.
   */
  readonly moveSpeedCampaign: number;
  /**
   * Session-27 (2026-07-24) — Figures in the front rank. Retires the
   * global `cohort_size_infantry` / `cohort_size_siege` dials in favour
   * of per-cohort authoring. Depth = figureCount / frontWidth.
   * Blank xlsx cell → 12 (infantry default).
   */
  readonly frontWidth: number;
  /** Recruitment costs per ECONOMY.md §4.2. */
  readonly goldCost: number;
  readonly ironCost: number;
  readonly woodCost: number;
  readonly horsesCost: number;
  readonly suppliesCost: number;
  readonly populationCost: number;
  readonly recruitTimeSec: number;
  /**
   * Structure prerequisites — an empty array = no prereq. Comma-separated
   * cell values imply AND (ALL listed structures must be built in the
   * recruiting province); enforced by apply-commands.ts recruit_cohort.
   */
  readonly structurePrereq: readonly string[];
  /**
   * MFix9 (2026-07-24) — transport slots for Ship-tagged cohorts. 0 for
   * non-Ship cohorts. Runtime consumer lands with fleet mechanics
   * post-MVP; the field is stored on the spec so xlsx tuning surfaces
   * cleanly the moment Ship logic ships.
   */
  readonly transportCapacity: number;
  /** Free-text notes from the xlsx (design rationale, playtest flags, etc.). */
  readonly notes: string;
}

export type Cohorts = Readonly<Record<string, CohortSpec>>;

/** Load the 6 basic cohorts. Fails loudly on schemaVersion mismatch. */
export function loadCohorts(): Cohorts {
  assertSchemaVersion(rawBlob, BALANCE_SCHEMA_VERSION, 'src/data/generated/dials.json');
  return (rawBlob as unknown as { cohorts: Cohorts }).cohorts;
}

/** Retrieve a cohort by name; throw on unknown names. */
export function getCohort(cohorts: Cohorts, name: string): CohortSpec {
  const entry = cohorts[name];
  if (!entry) {
    throw new Error(`Unknown cohort: ${name}. Known: ${Object.keys(cohorts).join(', ')}`);
  }
  return entry;
}
